/**
 * ClubsPanel — Admin catalogue management for lc_clubs.
 *
 * Mounted as the "Clubs" tab in the existing /admin shell. Founder spec
 * (25 May 2026): grow the existing admin shell panel-by-panel — this
 * is the first new panel, and the template for future ones (Programme
 * Benefits UI, Tenant Config, Member Profile Vault).
 *
 * Capabilities (v1):
 *  • Filterable list (country · city · facility · active)
 *  • Name substring search
 *  • Row click → drawer with all 15 fields + facility checklist
 *  • Add club
 *  • Soft-delete (Deactivate / Reactivate) toggle
 *  • XLSX upload → idempotent re-import
 *
 * Acceptance criterion (founder): deactivate a club here → confirm it
 * vanishes from Mira's /api/lc/clubs/search live.
 */

import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { resizeImageForUpload } from "@/lib/imageResize";

const CANONICAL_FACILITIES = [
  "Golf", "Indoor Games", "Sports & Fitness", "Health & Wellness",
  "Dining", "Accommodation", "Swimming", "Bar",
];

// ─── Small utilities ────────────────────────────────────────────────────────
function pillStyle(on) {
  return {
    padding: "0.35rem 0.85rem",
    borderRadius: 999,
    border: on ? "none" : "1px solid hsl(var(--brass) / 0.5)",
    background: on ? "hsl(var(--brass))" : "transparent",
    color: on ? "hsl(var(--ivory))" : "hsl(var(--navy))",
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontWeight: 600,
    cursor: "pointer",
  };
}


// ─── Drawer (right side panel) ──────────────────────────────────────────────
function ClubDrawer({ open, club, onClose, onSaved, onDeleted }) {
  const isNew = !club || !club.id;
  const empty = useMemo(() => ({
    name: "", address: "", country: "", state: "", city: "",
    postcode: "", opening_hour: "", dress_code: "", additional_charges: "",
    club_rules: "", short_description: "", long_description: "",
    image_url: "", other_amenities_raw: "", facility_tokens: [],
    active: true,
  }), []);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    if (isNew) setForm(empty);
    else setForm({ ...empty, ...club, facility_tokens: club.facility_tokens || [] });
  }, [isNew, club, empty]);

  const setKey = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleFacility = (tok) => () => {
    setForm((f) => {
      const current = new Set(f.facility_tokens || []);
      if (current.has(tok)) current.delete(tok); else current.add(tok);
      return { ...f, facility_tokens: Array.from(current) };
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (isNew) {
        const { active, ...rest } = form;  // eslint-disable-line no-unused-vars
        const created = await api.post("/admin/clubs", rest);
        onSaved && onSaved(created.data, "created");
      } else {
        const updated = await api.patch(`/admin/clubs/${club.id}`, form);
        onSaved && onSaved(updated.data, "updated");
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm(`Deactivate "${club.name}"? It will stop appearing in Mira's results.`)) return;
    setSaving(true); setErr("");
    try {
      await api.delete(`/admin/clubs/${club.id}`);
      onDeleted && onDeleted(club.id);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const reactivate = async () => {
    setSaving(true); setErr("");
    try {
      const updated = await api.patch(`/admin/clubs/${club.id}`, { active: true });
      onSaved && onSaved(updated.data, "updated");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div data-testid="clubs-drawer" style={{
      position: "fixed", top: 0, right: 0, bottom: 0,
      width: "min(720px, 92vw)",
      background: "hsl(var(--ivory))",
      borderLeft: "1px solid hsl(var(--brass) / 0.4)",
      boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
      zIndex: 50, overflowY: "auto", padding: "2rem 2.25rem",
    }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow">{isNew ? "Add a new club" : "Edit club"}</p>
          <h2 className="font-serif text-2xl mt-2">{form.name || "—"}</h2>
        </div>
        <button onClick={onClose} data-testid="clubs-drawer-close" className="btn-ghost">Close</button>
      </div>

      {!isNew && (
        <div className="mb-5 flex items-center gap-3">
          <span style={{
            fontSize: "0.72rem", letterSpacing: "0.18em",
            textTransform: "uppercase", fontWeight: 600,
            padding: "0.28rem 0.7rem", borderRadius: 4,
            background: form.active ? "rgba(15,61,46,0.1)" : "rgba(200,80,80,0.1)",
            color: form.active ? "rgb(15,61,46)" : "rgb(160,50,50)",
          }}>
            {form.active ? "Active" : "Deactivated"}
          </span>
          <span style={{ fontSize: "0.72rem", color: "hsl(var(--muted))" }}>
            id: {club.id?.slice(0,8)}{club.unique_id ? ` · catalogue id: ${club.unique_id}` : ""}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name *" testid="clubs-field-name">
          <input value={form.name || ""} onChange={setKey("name")} />
        </Field>
        <Field label="Country" testid="clubs-field-country">
          <input value={form.country || ""} onChange={setKey("country")} />
        </Field>
        <Field label="State / Region" testid="clubs-field-state">
          <input value={form.state || ""} onChange={setKey("state")} />
        </Field>
        <Field label="City" testid="clubs-field-city">
          <input value={form.city || ""} onChange={setKey("city")} />
        </Field>
        <Field label="Postcode" testid="clubs-field-postcode">
          <input value={form.postcode || ""} onChange={setKey("postcode")} />
        </Field>
        <Field label="Opening hours" testid="clubs-field-opening">
          <input value={form.opening_hour || ""} onChange={setKey("opening_hour")} />
        </Field>
        <Field label="Dress code" testid="clubs-field-dress">
          <input value={form.dress_code || ""} onChange={setKey("dress_code")} />
        </Field>
        <Field label="Additional charges" testid="clubs-field-charges">
          <input value={form.additional_charges || ""} onChange={setKey("additional_charges")} />
        </Field>
        <Field label="Image URL" testid="clubs-field-image" full>
          <div className="space-y-2">
            <input value={form.image_url || ""} onChange={setKey("image_url")} />
            {/* FIR-20260612-011 · upload-to-Cloudinary shortcut.
                 Calls existing /api/club/admin/clubs/{id_short}/image
                 endpoint which stores the file in Cloudinary folder
                 lc/clubs/{id_short} and writes the secure_url back
                 onto img + cloudinary_url + image_url. */}
            {!isNew && form.id_short && (
              <div className="flex items-center gap-3">
                {form.image_url && (
                  <img src={form.image_url} alt="" style={{
                    width: 80, height: 60, objectFit: "cover", borderRadius: 6,
                    border: "1px solid hsl(var(--brass) / 0.3)",
                  }} />
                )}
                <label className="text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-md border border-[hsl(var(--brass)/0.5)] text-[hsl(var(--navy))] hover:bg-[hsl(var(--brass)/0.10)] cursor-pointer">
                  {form.image_url ? "Replace image" : "Upload image"}
                  <input type="file" accept="image/*" className="hidden"
                         data-testid="clubs-field-image-upload"
                         onChange={async (e) => {
                           const raw = e.target.files?.[0]; if (!raw) return;
                           const file = await resizeImageForUpload(raw);
                           const fd = new FormData(); fd.append("file", file);
                           try {
                             const { data } = await api.post(
                               `/club/admin/clubs/${form.id_short}/image`, fd,
                               { headers: { "Content-Type": "multipart/form-data" } },
                             );
                             const url = data?.club?.img || data?.club?.image_url || data?.url;
                             if (url) setForm((f) => ({ ...f, image_url: url }));
                           } catch (err) {
                             setErr(formatApiError(err?.response?.data?.detail) || err.message);
                           } finally {
                             e.target.value = "";
                           }
                         }} />
                </label>
                <span style={{ fontSize: 11, color: "hsl(var(--muted))" }}>
                  Stored in Cloudinary · lc/clubs/{form.id_short}
                </span>
              </div>
            )}
            {isNew && (
              <p className="text-[11px] italic" style={{ color: "hsl(var(--muted))" }}>
                Save the club first, then re-open to upload an image.
              </p>
            )}
          </div>
        </Field>
        <Field label="Address" testid="clubs-field-address" full>
          <textarea value={form.address || ""} onChange={setKey("address")} rows={2} />
        </Field>
        <Field label="Club rules" testid="clubs-field-rules" full>
          <textarea value={form.club_rules || ""} onChange={setKey("club_rules")} rows={2} />
        </Field>
        <Field label="Short description" testid="clubs-field-short" full>
          <textarea value={form.short_description || ""} onChange={setKey("short_description")} rows={2} />
        </Field>
        <Field label="Long description" testid="clubs-field-long" full>
          <textarea value={form.long_description || ""} onChange={setKey("long_description")} rows={4} />
        </Field>
        <Field label="Other Amenities (raw — comma-separated)" testid="clubs-field-amenities" full>
          <input value={form.other_amenities_raw || ""} onChange={setKey("other_amenities_raw")} placeholder="Golf, Dining, Bar" />
          <p className="mt-1 text-[11px] italic" style={{ color: "hsl(var(--muted))" }}>
            Normalised into the canonical facility tokens below on save.
          </p>
        </Field>
      </div>

      <div className="mt-6">
        <p className="eyebrow">Facility tokens</p>
        <p className="mt-1 text-[12px] italic" style={{ color: "hsl(var(--muted))" }}>
          Drives Mira&apos;s lookup (e.g. <code>facility=Golf</code>). Toggle each that this club offers.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {CANONICAL_FACILITIES.map((tok) => {
            const on = (form.facility_tokens || []).includes(tok);
            return (
              <button key={tok} onClick={toggleFacility(tok)}
                      data-testid={`clubs-facility-${tok.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                      style={pillStyle(on)}>
                {tok}
              </button>
            );
          })}
        </div>
      </div>

      {err && <p className="mt-5 text-[13px]" style={{ color: "rgb(160,50,50)" }}>{err}</p>}

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={saving || !form.name}
                data-testid="clubs-drawer-save" className="btn-primary">
          {saving ? "Saving…" : (isNew ? "Create club" : "Save changes")}
        </button>
        {!isNew && form.active && (
          <button onClick={deactivate} disabled={saving}
                  data-testid="clubs-drawer-deactivate"
                  style={{
                    padding: "0.7rem 1.1rem", borderRadius: 4,
                    border: "1px solid rgba(160,50,50,0.5)",
                    background: "transparent", color: "rgb(160,50,50)",
                    fontSize: "0.78rem", letterSpacing: "0.06em",
                    textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
                  }}>
            Deactivate
          </button>
        )}
        {!isNew && !form.active && (
          <button onClick={reactivate} disabled={saving}
                  data-testid="clubs-drawer-reactivate" className="btn-ghost">
            Reactivate
          </button>
        )}
      </div>
    </div>
  );
}


function Field({ label, children, full, testid }) {
  return (
    <div data-testid={testid} className={full ? "sm:col-span-2" : ""}>
      <label className="text-[11px] tracking-[0.22em] uppercase font-semibold"
             style={{ color: "hsl(var(--muted))" }}>
        {label}
      </label>
      <div className="mt-1.5">
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            style: {
              width: "100%",
              padding: "0.55rem 0.7rem",
              border: "1px solid hsl(var(--brass) / 0.4)",
              borderRadius: 4, fontSize: "0.85rem",
              fontFamily: "inherit", background: "white",
              outline: "none",
            },
          }))}
      </div>
    </div>
  );
}


// ─── Importer panel ─────────────────────────────────────────────────────────
function ImporterCard({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/clubs/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      onDone && onDone();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="clubs-importer" style={{
      border: "1px dashed hsl(var(--brass) / 0.5)",
      borderRadius: 6, padding: "1.1rem 1.25rem", marginBottom: 16,
    }}>
      <p className="eyebrow">Re-import the master catalogue</p>
      <p className="mt-2 text-[13px]" style={{ color: "hsl(var(--muted))" }}>
        Upload an updated <code>.xlsx</code>; existing clubs are matched by their <code>Unique Id</code>
        and updated. New rows are inserted. Nothing is duplicated.
      </p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <input type="file" accept=".xlsx,.xlsm"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               data-testid="clubs-importer-file" />
        <button onClick={submit} disabled={!file || busy}
                data-testid="clubs-importer-submit" className="btn-primary">
          {busy ? "Importing…" : "Run import"}
        </button>
      </div>
      {result && (
        <p data-testid="clubs-importer-result" className="mt-3 text-[13px]"
           style={{ color: "rgb(15,61,46)" }}>
          ✓ {result.inserted} inserted · {result.updated} updated ·
          {" "}{result.skipped_blank} skipped · {result.errors} errors
          {result.error_samples?.length ? ` — first: ${result.error_samples[0]}` : ""}
        </p>
      )}
      {err && <p className="mt-3 text-[13px]" style={{ color: "rgb(160,50,50)" }}>{err}</p>}
    </div>
  );
}


// ─── Main panel ─────────────────────────────────────────────────────────────
export default function ClubsPanel() {
  const [filters, setFilters] = useState({
    country: "", city: "", facility: "", active: "true", q: "",
  });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [facets, setFacets] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const loadFacets = async () => {
    try {
      const res = await api.get("/admin/clubs/facets");
      setFacets(res.data);
    } catch (e) { /* non-fatal */ }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { skip: page * pageSize, limit: pageSize };
      if (filters.country) params.country = filters.country;
      if (filters.city) params.city = filters.city;
      if (filters.facility) params.facility = filters.facility;
      if (filters.active !== "") params.active = filters.active;
      if (filters.q) params.q = filters.q;
      const res = await api.get("/admin/clubs", { params });
      setRows(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFacets(); }, []);   // once
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const onFilterChange = (k) => (e) => {
    setFilters((f) => ({ ...f, [k]: e.target.value }));
    setPage(0);
  };
  const applyFilters = (e) => { e.preventDefault(); setPage(0); load(); };

  const openNew = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (club) => { setEditing(club); setDrawerOpen(true); };

  const handleSaved = (club, action) => {
    setDrawerOpen(false);
    setEditing(null);
    load(); loadFacets();
  };
  const handleDeleted = () => {
    setDrawerOpen(false);
    setEditing(null);
    load(); loadFacets();
  };

  return (
    <div data-testid="admin-clubs-panel" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Operations · Catalogue</p>
          <h2 className="font-serif text-2xl mt-2">Club catalogue</h2>
          {facets && (
            <p className="mt-1 text-[12px] italic" style={{ color: "hsl(var(--muted))" }}>
              {facets.total_active.toLocaleString()} active · {facets.total_inactive.toLocaleString()} deactivated ·
              {" "}{facets.total.toLocaleString()} total across {facets.countries.length} countries
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={async () => {
            // CSV export — respects the active filter set so the founder
            // can export "all Mumbai golf clubs", "all deactivated", etc.
            try {
              const qs = new URLSearchParams();
              if (filters.country) qs.set("country", filters.country);
              if (filters.city) qs.set("city", filters.city);
              if (filters.facility) qs.set("facility", filters.facility);
              if (filters.active !== "") qs.set("active", filters.active);
              if (filters.q) qs.set("q", filters.q);
              const res = await api.get(`/admin/clubs/export.csv?${qs.toString()}`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
              const a = document.createElement("a");
              a.href = url;
              const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
              a.download = `lc_clubs-${ts}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              setErr(formatApiError(e?.response?.data?.detail) || e.message);
            }
          }} data-testid="clubs-export-button" className="btn-ghost">Export CSV</button>
          <button onClick={openNew} className="btn-primary"
                  data-testid="clubs-add-button">+ Add a club</button>
        </div>
      </div>

      <ImporterCard onDone={() => { load(); loadFacets(); }} />

      <form onSubmit={applyFilters} className="grid grid-cols-1 sm:grid-cols-5 gap-3"
            data-testid="clubs-filters">
        <input placeholder="Country" value={filters.country} onChange={onFilterChange("country")}
               data-testid="clubs-filter-country"
               style={{ padding: "0.55rem 0.7rem", border: "1px solid hsl(var(--brass) / 0.4)", borderRadius: 4, fontSize: "0.85rem" }} />
        <input placeholder="City" value={filters.city} onChange={onFilterChange("city")}
               data-testid="clubs-filter-city"
               style={{ padding: "0.55rem 0.7rem", border: "1px solid hsl(var(--brass) / 0.4)", borderRadius: 4, fontSize: "0.85rem" }} />
        <select value={filters.facility} onChange={onFilterChange("facility")}
                data-testid="clubs-filter-facility"
                style={{ padding: "0.55rem 0.7rem", border: "1px solid hsl(var(--brass) / 0.4)", borderRadius: 4, fontSize: "0.85rem", background: "white" }}>
          <option value="">All facilities</option>
          {CANONICAL_FACILITIES.map((f) => <option key={f}>{f}</option>)}
        </select>
        <select value={filters.active} onChange={onFilterChange("active")}
                data-testid="clubs-filter-active"
                style={{ padding: "0.55rem 0.7rem", border: "1px solid hsl(var(--brass) / 0.4)", borderRadius: 4, fontSize: "0.85rem", background: "white" }}>
          <option value="">All</option>
          <option value="true">Active only</option>
          <option value="false">Deactivated only</option>
        </select>
        <div className="flex gap-2">
          <input placeholder="Search by name" value={filters.q} onChange={onFilterChange("q")}
                 data-testid="clubs-filter-q"
                 style={{ flex: 1, padding: "0.55rem 0.7rem", border: "1px solid hsl(var(--brass) / 0.4)", borderRadius: 4, fontSize: "0.85rem" }} />
          <button type="submit" data-testid="clubs-filter-apply" className="btn-ghost">Apply</button>
        </div>
      </form>

      {err && <p className="text-[13px]" style={{ color: "rgb(160,50,50)" }}>{err}</p>}

      <div className="text-[12px]" style={{ color: "hsl(var(--muted))" }}>
        {loading ? "Loading…" : `${total.toLocaleString()} match${total === 1 ? "" : "es"}`}
      </div>

      <div data-testid="clubs-table" style={{ border: "1px solid hsl(var(--brass) / 0.2)", borderRadius: 4 }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 2.5fr) 1fr 1fr 1.5fr 0.7fr",
          gap: 0,
          fontSize: "0.72rem",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          padding: "0.7rem 1rem",
          background: "hsl(var(--brass) / 0.06)",
          borderBottom: "1px solid hsl(var(--brass) / 0.25)",
        }}>
          <div>Name</div><div>City</div><div>Country</div><div>Facilities</div><div>Status</div>
        </div>
        {rows && rows.length === 0 && (
          <p className="p-4 text-[13px] italic" style={{ color: "hsl(var(--muted))" }}>No clubs match these filters.</p>
        )}
        {rows && rows.map((c) => (
          <button key={c.id}
                  data-testid={`clubs-row-${c.id}`}
                  onClick={() => openEdit(c)}
                  style={{
                    display: "grid", width: "100%",
                    gridTemplateColumns: "minmax(220px, 2.5fr) 1fr 1fr 1.5fr 0.7fr",
                    gap: 0, textAlign: "left", padding: "0.85rem 1rem",
                    borderBottom: "1px solid hsl(var(--brass) / 0.12)",
                    background: "white", cursor: "pointer", fontSize: "0.82rem",
                  }}>
            <div style={{ fontWeight: 500 }}>{c.name}</div>
            <div>{c.city || "—"}</div>
            <div>{c.country || "—"}</div>
            <div style={{ fontSize: "0.74rem", color: "hsl(var(--muted))" }}>
              {(c.facility_tokens || []).join(" · ") || "—"}
            </div>
            <div>
              <span style={{
                fontSize: "0.66rem", letterSpacing: "0.14em",
                textTransform: "uppercase", fontWeight: 600,
                padding: "0.18rem 0.55rem", borderRadius: 3,
                background: c.active ? "rgba(15,61,46,0.08)" : "rgba(160,50,50,0.08)",
                color: c.active ? "rgb(15,61,46)" : "rgb(160,50,50)",
              }}>
                {c.active ? "Active" : "Off"}
              </span>
            </div>
          </button>
        ))}
      </div>

      {total > pageSize && (
        <div className="flex items-center gap-3" data-testid="clubs-pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="btn-ghost">
            ← Prev
          </button>
          <span className="text-[12px]" style={{ color: "hsl(var(--muted))" }}>
            Page {page + 1} of {Math.max(1, Math.ceil(total / pageSize))}
          </span>
          <button disabled={(page + 1) * pageSize >= total}
                  onClick={() => setPage((p) => p + 1)} className="btn-ghost">
            Next →
          </button>
        </div>
      )}

      <ClubDrawer
        open={drawerOpen}
        club={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </div>
  );
}
